import { Brain, Settings, Zap } from "lucide-react";
import { useLocation } from "wouter";
import type { ChatCompletionParams } from "../../services/playground";
import type { RouteConfig } from "../../services/route-configs";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";

interface PlaygroundConfigPanelProps {
	routeConfig: RouteConfig | null;
	params: ChatCompletionParams;
	systemPrompt: string;
	onParamsChange: (params: ChatCompletionParams) => void;
	onSystemPromptChange: (prompt: string) => void;
}

function ParametersTab({
	routeConfig,
	params,
	systemPrompt,
	onParamsChange,
	onSystemPromptChange,
}: {
	routeConfig: RouteConfig | null;
	params: ChatCompletionParams;
	systemPrompt: string;
	onParamsChange: (params: ChatCompletionParams) => void;
	onSystemPromptChange: (prompt: string) => void;
}) {
	if (!routeConfig) {
		return (
			<div className="p-4 text-center text-muted-foreground">
				<Settings className="h-8 w-8 mx-auto mb-2 opacity-50" />
				<p className="text-sm">Select a route config to configure parameters</p>
			</div>
		);
	}

	const primaryModelConfig = routeConfig.models?.find(
		(m) => m.isPrimary,
	)?.modelConfig;

	return (
		<div className="p-4 space-y-6">
			<div className="space-y-2">
				<h4 className="text-sm font-medium">Selected Route Config</h4>
				<div className="p-3 bg-muted/50 rounded-lg space-y-1">
					<p className="font-medium">{routeConfig.name}</p>
					{primaryModelConfig && (
						<>
							<p className="text-xs text-muted-foreground">
								{primaryModelConfig.providerType} /{" "}
								{primaryModelConfig.modelIdentifier}
							</p>
							<div className="flex gap-2 mt-2">
								{routeConfig.hotCacheEnabled && (
									<Badge variant="outline" className="text-[10px]">
										<Zap className="h-3 w-3 mr-1" />
										Hot Cache
									</Badge>
								)}
								{routeConfig.semanticCacheEnabled && (
									<Badge variant="outline" className="text-[10px]">
										<Brain className="h-3 w-3 mr-1" />
										Semantic Cache
									</Badge>
								)}
							</div>
						</>
					)}
				</div>
			</div>

			<div className="space-y-2">
				<div className="flex items-center justify-between">
					<label className="text-sm font-medium">Temperature</label>
					<span className="text-xs text-muted-foreground font-mono">
						{params.temperature ?? 0.7}
					</span>
				</div>
				<input
					type="range"
					min={0}
					max={2}
					step={0.1}
					value={params.temperature ?? 0.7}
					onChange={(e) =>
						onParamsChange({
							...params,
							temperature: parseFloat(e.target.value),
						})
					}
					className="w-full"
				/>
				<p className="text-xs text-muted-foreground">
					Lower = more focused, Higher = more creative
				</p>
			</div>

			<div className="space-y-2">
				<label className="text-sm font-medium">Max Tokens</label>
				<Input
					type="number"
					min={1}
					max={32000}
					value={params.max_tokens ?? 2048}
					onChange={(e) =>
						onParamsChange({
							...params,
							max_tokens: parseInt(e.target.value, 10) || 2048,
						})
					}
				/>
				<p className="text-xs text-muted-foreground">Maximum response length</p>
			</div>

			<div className="space-y-2">
				<div className="flex items-center justify-between">
					<label className="text-sm font-medium">Top P</label>
					<span className="text-xs text-muted-foreground font-mono">
						{params.top_p ?? 0.9}
					</span>
				</div>
				<input
					type="range"
					min={0}
					max={1}
					step={0.05}
					value={params.top_p ?? 0.9}
					onChange={(e) =>
						onParamsChange({ ...params, top_p: parseFloat(e.target.value) })
					}
					className="w-full"
				/>
			</div>

			<div className="space-y-2">
				<label className="text-sm font-medium">System Prompt Override</label>
				<Textarea
					placeholder={routeConfig.systemPrompt || "Enter system prompt..."}
					value={systemPrompt}
					onChange={(e) => onSystemPromptChange(e.target.value)}
					rows={4}
					className="text-sm"
				/>
				<p className="text-xs text-muted-foreground">
					Temporary override for this session
				</p>
			</div>

			<div className="space-y-3">
				<h4 className="text-sm font-medium">Cache Options</h4>
				<div className="flex items-center justify-between">
					<label className="text-sm">Skip Hot Cache</label>
					<Switch
						checked={params.nova?.skipCache ?? false}
						onCheckedChange={(v) =>
							onParamsChange({
								...params,
								nova: { ...params.nova, skipCache: v },
							})
						}
					/>
				</div>
			</div>

			{routeConfig.responseFormat && (
				<div className="space-y-2 pt-4 border-t">
					<h4 className="text-sm font-medium">Response Format</h4>
					<div className="p-3 bg-muted/50 rounded-lg space-y-2">
						<Badge variant="outline" className="text-xs">
							{routeConfig.responseFormat.type}
						</Badge>
						{routeConfig.responseFormat.type === "classification" && (
							<p className="text-xs text-muted-foreground">
								Categories:{" "}
								{routeConfig.responseFormat.categories?.join(", ") || "None"}
							</p>
						)}
						{routeConfig.responseFormat.type === "structured" && (
							<p className="text-xs text-muted-foreground">
								JSON Schema configured
							</p>
						)}
						{routeConfig.responseFormat.type === "custom" && (
							<p className="text-xs text-muted-foreground">
								Format: {routeConfig.responseFormat.format || "N/A"}
							</p>
						)}
						<p className="text-xs text-muted-foreground mt-2">
							Configured at route config level (read-only)
						</p>
					</div>
				</div>
			)}
		</div>
	);
}

export function PlaygroundConfigPanel({
	routeConfig,
	params,
	systemPrompt,
	onParamsChange,
	onSystemPromptChange,
}: PlaygroundConfigPanelProps) {
	const [, navigate] = useLocation();
	return (
		<div className="h-full bg-card flex flex-col">
			<div className="flex items-center justify-between px-4 py-3 border-b bg-card sticky top-0 z-10">
				<div className="flex items-center gap-2 text-sm font-medium">
					<Settings className="h-4 w-4" />
					Tuning
				</div>
				<Button
					variant="outline"
					size="sm"
					onClick={() => navigate("/route-configs/new")}
				>
					Open Config Wizard
				</Button>
			</div>
			<div className="flex-1 overflow-y-auto p-4">
				<ParametersTab
					routeConfig={routeConfig}
					params={params}
					systemPrompt={systemPrompt}
					onParamsChange={onParamsChange}
					onSystemPromptChange={onSystemPromptChange}
				/>
			</div>
		</div>
	);
}
